import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useToasts } from './TactileToasts';
import { useConfirm } from '../context/ConfirmContext';
import { usePrompt } from '../context/PromptContext';

interface DashboardMetrics {
  timestamp: string;
  public: {
    totalUsers: number;
    totalStylesGenerated: number;
    totalItemsUploaded: number;
    onboardedTiers: {
      Explorer: number;
      Standard: number;
      Premium: number;
    };
  };
  personal?: {
    tier: string;
    credits: number;
    expiryDate: string;
    nextCycleTier?: string;
    itemsUploaded: number;
    maxItemsLimit: number;
  };
  adminUserCouponTracking?: Array<{
    userId: string;
    userDetails?: string;
    tier: string;
    usedPromoCodes: string[];
    expiryDate?: string;
    daysToExpire: number;
    isPermanentAdmin?: boolean;
    lastActive?: string;
  }>;
  adminInvoices?: Array<{
    id: string;
    invoiceNumber: string;
    userId: string;
    userEmail: string;
    tier: string;
    type: string;
    currency: string;
    basePrice: number;
    discountAmount: number;
    couponCode?: string;
    totalAmount: number;
    status: 'PAID' | 'REFUNDED';
    createdAt: string;
    paymentGateway: string;
    transactionId: string;
  }>;
  adminCoupons?: Array<{
    id: string;
    type: string;
    value: number;
    applicableTiers: string[];
    timesRedeemed: number;
    isActive: boolean;
    description?: string;
    isSystem?: boolean;
  }>;
  operations?: {
    totalStorageUsedBytes: number;
    activeUsersCount24h: number;
    retentionRateMonthOverMonth: number;
    averageImageSizeBytes: number;
    heavyUploaders: Array<{ userIdHash: string; uploadCount: number; avgSizeMB: number }>;
    frequentLimiterHits: Array<{ userIdHash: string; hitCount: number }>;
    averageAiResponseTimeMs: number;
    averageImageGenTimeMs: number;
    errorRatePercent: number;
  };
}

const Dashboard: React.FC = () => {
  const { showToast } = useToasts();
  const { confirm } = useConfirm();
  const { prompt } = usePrompt();

  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Admin coupon creator modal
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [newCouponCode, setNewCouponCode] = useState('');
  const [newCouponType, setNewCouponType] = useState<'DISCOUNT_PERCENT' | 'TRIAL_ACCESS'>('DISCOUNT_PERCENT');
  const [newCouponValue, setNewCouponValue] = useState('50');
  const [newCouponDesc, setNewCouponDesc] = useState('');

  // User search filter
  const [userSearch, setUserSearch] = useState('');

  const fetchMetrics = useCallback(async () => {
    try {
      const response = await fetch('/api/dashboard');
      if (!response.ok) {
        throw new Error('Failed to fetch platform telemetry.');
      }
      const data = await response.json();
      setMetrics(data);
    } catch (err: any) {
      setError(err.message || 'Telemetry offline.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, 60000);
    return () => clearInterval(interval);
  }, [fetchMetrics]);

  const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  const handleIssueRefund = async (invoiceId: string, invoiceNumber: string, amount: number, currency: string) => {
    const isConfirmed = await confirm(
      `Issue Refund for ${invoiceNumber}?`,
      `This will refund ${currency} ${amount.toFixed(2)} to the user, mark the invoice as REFUNDED, and adjust their subscription status.`
    );
    if (!isConfirmed) return;

    const reason = await prompt("Refund Reason", "Enter administrative reason for this refund:", "Customer requested refund");
    if (reason === null) return;

    try {
      showToast(`Processing refund for ${invoiceNumber}...`, "sync");
      const res = await fetch('/api/manageRefund', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceId, refundReason: reason })
      });
      const data = await res.json();

      if (res.ok) {
        showToast(`✨ Invoice ${invoiceNumber} refunded successfully!`, "success");
        fetchMetrics();
      } else {
        showToast(data.error || "Refund processing failed.", "error");
      }
    } catch (e: any) {
      showToast("Refund API execution failed.", "error");
    }
  };

  const handleCreateCoupon = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCouponCode.trim()) return;

    try {
      showToast(`Creating promo code ${newCouponCode}...`, "logic");
      const res = await fetch('/api/manageCoupons', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: newCouponCode.trim().toUpperCase(),
          type: newCouponType,
          value: parseFloat(newCouponValue),
          applicableTiers: ['All'],
          description: newCouponDesc || `Promo code ${newCouponCode.toUpperCase()}`
        })
      });

      const data = await res.json();
      if (res.ok) {
        showToast(`✨ Promo code ${newCouponCode.toUpperCase()} created!`, "success");
        setShowCouponModal(false);
        setNewCouponCode('');
        setNewCouponDesc('');
        fetchMetrics();
      } else {
        showToast(data.error || "Failed to create promo code.", "error");
      }
    } catch (e: any) {
      showToast("Coupon API execution failed.", "error");
    }
  };

  const handleDeleteCoupon = async (couponId: string) => {
    const isConfirmed = await confirm(`Delete Coupon ${couponId}?`, `Are you sure you want to delete promo code ${couponId}?`);
    if (!isConfirmed) return;

    try {
      const res = await fetch(`/api/manageCoupons?id=${encodeURIComponent(couponId)}`, { method: 'DELETE' });
      if (res.ok) {
        showToast(`Coupon ${couponId} deleted.`, "success");
        fetchMetrics();
      } else {
        showToast("Could not delete coupon.", "error");
      }
    } catch (e) {
      showToast("Delete failed.", "error");
    }
  };

  const handleAdminPurgeUser = async (targetUserId: string, targetHandle: string) => {
    const isConfirmed = await confirm(
      `Purge User Account?`,
      `Permanently purge user '${targetHandle || targetUserId}' and all associated closet data from the system? This cannot be undone.`
    );
    if (!isConfirmed) return;

    try {
      let res = await fetch('/api/adminDeleteUser', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: targetUserId })
      });

      // Fallback for preview deployments where adminDeleteUser is not yet registered
      if (res.status === 404) {
        res = await fetch('/api/deleteAccount', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: targetUserId })
        });
      }

      const data = await res.json();
      if (res.ok) {
        showToast(`User '${targetHandle || targetUserId}' purged successfully.`, "success");
        fetchMetrics();
      } else {
        showToast(data.error || "Failed to purge user.", "error");
      }
    } catch (e) {
      showToast("Purge request failed.", "error");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center gap-4">
        <div className="w-12 h-12 border-2 border-azure/20 border-t-azure rounded-full animate-spin"></div>
        <p className="text-[10px] font-black text-white uppercase tracking-[0.3em] animate-pulse">Syncing Telemetry Database</p>
      </div>
    );
  }

  if (error || !metrics) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center text-center p-4">
        <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center border border-red-500/20 mb-6">
          <svg className="w-8 h-8 text-red-500 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
        </div>
        <h2 className="text-xl font-black text-white uppercase tracking-wider">System Offline</h2>
        <p className="text-xs text-gray-500 uppercase tracking-widest mt-2 max-w-xs">{error || 'Metrics snapshot unavailable'}</p>
        <Link to="/" className="btn-azure mt-8 py-2 px-6 text-[10px] uppercase tracking-widest">Return to Core Engine</Link>
      </div>
    );
  }

  const { public: pub, personal, operations: ops } = metrics;
  const isOperator = !!ops;

  const filteredUsers = (metrics.adminUserCouponTracking || []).filter((u) => {
    if (!userSearch.trim()) return true;
    const term = userSearch.toLowerCase();
    return u.userId.toLowerCase().includes(term) || (u.userDetails && u.userDetails.toLowerCase().includes(term)) || u.tier.toLowerCase().includes(term);
  });

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-gray-200 p-4 md:p-8 flex flex-col items-center font-inter select-none">
      
      {/* Top Banner */}
      <div className="text-center mb-8 md:mb-12 mt-4 animate-fade-in max-w-2xl">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-azure/10 border border-azure/20 rounded-full text-[8px] font-black text-azure-400 uppercase tracking-widest mb-4">
          <span className="w-1.5 h-1.5 bg-azure rounded-full animate-ping"></span> Live Metrics & Commerce Hub
        </div>
        <h1 className="text-2xl md:text-4xl font-black text-white italic tracking-tighter uppercase leading-none mb-4">
          SYSTEM <span className="text-azure-500">TELEMETRY</span>
        </h1>
        <p className="text-[9px] md:text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">
          Last Synced: {new Date(metrics.timestamp).toLocaleTimeString()} (Hourly Aggregations)
        </p>
      </div>

      <div className="w-full max-w-5xl space-y-8 md:space-y-12 animate-fade-in">
        
        {/* SECTION 1: PERSONAL TELEMETRY */}
        {personal && (
          <section className="glass-panel p-6 rounded-2xl border border-azure/20 bg-azure/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-azure/10 blur-3xl rounded-full"></div>
            <h2 className="text-[10px] font-black uppercase text-azure-400 tracking-[0.2em] mb-6 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
              Personal Subscription Depot
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-[#0e0e0e]/60 border border-white/5 p-4 rounded-xl">
                <p className="text-[8px] font-black text-gray-500 uppercase tracking-widest">Active Tier</p>
                <p className="text-xl font-black text-white uppercase tracking-wider mt-1">{personal.tier}</p>
                {personal.nextCycleTier && (
                  <p className="text-[8px] font-bold text-orange-400 uppercase tracking-wider mt-1">Succession scheduled to {personal.nextCycleTier}</p>
                )}
              </div>

              <div className="bg-[#0e0e0e]/60 border border-white/5 p-4 rounded-xl">
                <p className="text-[8px] font-black text-gray-500 uppercase tracking-widest">Logic Balance</p>
                <p className="text-xl font-black text-gold-500 uppercase tracking-wider mt-1">
                  {personal.tier === 'Premium' ? 'Infinite Credits' : `${personal.credits} Credits`}
                </p>
                {personal.expiryDate && (
                  <p className="text-[8px] font-bold text-gray-400 uppercase tracking-wider mt-1">
                    Expires {new Date(personal.expiryDate).toLocaleDateString()}
                  </p>
                )}
              </div>

              <div className="bg-[#0e0e0e]/60 border border-white/5 p-4 rounded-xl flex flex-col justify-between">
                <div>
                  <div className="flex justify-between items-center text-[8px] font-black text-gray-500 uppercase tracking-widest mb-1">
                    <span>Digital Closet Space</span>
                    <span className="text-azure-400">{personal.itemsUploaded} / {personal.maxItemsLimit} Items</span>
                  </div>
                  <div className="w-full bg-white/5 rounded-full h-1.5 overflow-hidden border border-white/5">
                    <div 
                      className="bg-azure h-full transition-all duration-1000" 
                      style={{ width: `${(personal.itemsUploaded / personal.maxItemsLimit) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* SECTION 2: GLOBAL PUBLIC PLATFORM STATS */}
        <section className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-[10px] font-black uppercase text-azure-400 tracking-[0.2em] flex items-center gap-2">
              <svg className="w-4 h-4 text-azure-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path></svg>
              Global Platform Metrics
            </h2>
            <span className="text-[7px] font-black text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20 uppercase tracking-widest">Public Telemetry</span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="glass-panel p-4 rounded-xl bg-black/40 border border-white/5">
              <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest block">Total Registered Users</span>
              <span className="text-2xl font-black text-white font-mono mt-1 block">{pub.totalUsers}</span>
            </div>
            <div className="glass-panel p-4 rounded-xl bg-black/40 border border-white/5">
              <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest block">Styles Synthesized</span>
              <span className="text-2xl font-black text-azure-400 font-mono mt-1 block">{pub.totalStylesGenerated}</span>
            </div>
            <div className="glass-panel p-4 rounded-xl bg-black/40 border border-white/5">
              <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest block">Wardrobe Items Sync'd</span>
              <span className="text-2xl font-black text-gold-400 font-mono mt-1 block">{pub.totalItemsUploaded}</span>
            </div>
            <div className="glass-panel p-4 rounded-xl bg-black/40 border border-white/5">
              <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest block">Paid Adoption (Std + Prem)</span>
              <span className="text-2xl font-black text-emerald-400 font-mono mt-1 block">
                {pub.onboardedTiers.Standard + pub.onboardedTiers.Premium}
              </span>
            </div>
          </div>
        </section>

        {/* SECTION 3: ADMIN USER & COUPON LEDGER */}
        {metrics.adminUserCouponTracking && metrics.adminUserCouponTracking.length > 0 && (
          <section className="glass-panel p-6 rounded-2xl border border-azure/30 bg-azure/5 relative space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <span className="text-azure-400 text-[8px] font-black uppercase tracking-[0.3em] block mb-1">Admin Operations Vault</span>
                <h2 className="text-lg font-black uppercase text-white tracking-wider flex items-center gap-2">
                  <span>👥</span> User Accounts & Coupon Ledger
                </h2>
              </div>
              <div className="flex items-center gap-3 w-full md:w-auto">
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Search users..."
                  className="bg-black/60 border border-white/10 rounded-xl px-3 py-1.5 text-xs font-mono text-white focus:outline-none focus:border-azure-400 w-full md:w-48"
                />
                <span className="px-3 py-1.5 bg-azure/20 text-azure-300 border border-azure/30 text-[9px] font-black uppercase tracking-widest rounded-xl whitespace-nowrap">
                  {filteredUsers.length} Users
                </span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-gray-300 border-collapse">
                <thead>
                  <tr className="border-b border-white/10 text-[9px] font-black uppercase tracking-widest text-azure-400">
                    <th className="py-3 px-4">User ID / Handle</th>
                    <th className="py-3 px-4">Active Tier</th>
                    <th className="py-3 px-4">Redeemed Coupon(s)</th>
                    <th className="py-3 px-4">Days to Expire</th>
                    <th className="py-3 px-4">Expiry Date</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 font-mono">
                  {filteredUsers.map((u) => (
                    <tr key={u.userId} className="hover:bg-white/[0.02] transition-colors">
                      <td className="py-3 px-4 font-bold text-white flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-azure animate-pulse"></span>
                        <span>{u.userDetails || u.userId}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${
                          u.tier === 'Premium' ? 'bg-gold/20 text-gold border border-gold/30' :
                          u.tier === 'Standard' ? 'bg-azure/20 text-azure-300 border border-azure/30' :
                          'bg-white/5 text-gray-400 border border-white/10'
                        }`}>
                          {u.tier}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {u.usedPromoCodes && u.usedPromoCodes.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {u.usedPromoCodes.map((code) => (
                              <span key={code} className="px-2 py-0.5 bg-green-900/30 text-green-300 border border-green-500/30 rounded text-[9px] font-black uppercase tracking-wider">
                                {code}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-500 text-[10px] italic">No Coupon</span>
                        )}
                      </td>
                      <td className="py-3 px-4 font-bold">
                        {u.isPermanentAdmin ? (
                          <span className="text-gold">♾️ Admin</span>
                        ) : u.daysToExpire === 0 ? (
                          <span className="text-red-400">0 Days (Expired)</span>
                        ) : (
                          <span className="text-white">{u.daysToExpire} Days</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-gray-400 text-[10px]">
                        {u.isPermanentAdmin ? '2099-12-31' : (u.expiryDate ? new Date(u.expiryDate).toLocaleDateString() : 'N/A')}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {u.isPermanentAdmin ? (
                          <span className="text-[9px] font-black uppercase text-gold/60 tracking-wider">Protected</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleAdminPurgeUser(u.userId, u.userDetails || u.userId)}
                            className="px-2.5 py-1 text-[9px] font-black uppercase tracking-wider text-red-400 hover:text-white bg-red-950/30 hover:bg-red-600/60 border border-red-500/30 hover:border-red-500 rounded-lg transition-all"
                            title={`Purge user account ${u.userDetails || u.userId}`}
                          >
                            Purge ✕
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* SECTION 4: ADMIN INVOICES, BILLING & REFUND HUB */}
        {metrics.adminInvoices && (
          <section className="glass-panel p-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 relative space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <span className="text-emerald-400 text-[8px] font-black uppercase tracking-[0.3em] block mb-1">Commerce Ledger</span>
                <h2 className="text-lg font-black uppercase text-white tracking-wider flex items-center gap-2">
                  <span>🧾</span> Invoices, Payments & Admin Refunds
                </h2>
              </div>
              <span className="px-3 py-1 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[9px] font-black uppercase tracking-widest rounded-full">
                {metrics.adminInvoices.length} Transactions
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-gray-300 border-collapse">
                <thead>
                  <tr className="border-b border-white/10 text-[9px] font-black uppercase tracking-widest text-emerald-400">
                    <th className="py-3 px-4">Invoice No</th>
                    <th className="py-3 px-4">Customer Email</th>
                    <th className="py-3 px-4">Plan / Type</th>
                    <th className="py-3 px-4">Amount</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 font-mono">
                  {metrics.adminInvoices.map((inv) => (
                    <tr key={inv.id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="py-3 px-4 font-bold text-white">{inv.invoiceNumber}</td>
                      <td className="py-3 px-4 text-gray-300">{inv.userEmail}</td>
                      <td className="py-3 px-4 text-gray-400">{inv.tier} ({inv.type})</td>
                      <td className="py-3 px-4 font-bold text-emerald-400">{inv.currency} {inv.totalAmount?.toFixed(2)}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${
                          inv.status === 'PAID' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' :
                          'bg-red-500/20 text-red-300 border border-red-500/30'
                        }`}>
                          {inv.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right space-x-2">
                        <a
                          href={`/api/getInvoices?invoiceId=${inv.id}&format=html`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[10px] font-black text-azure-400 hover:text-white underline uppercase"
                        >
                          View PDF ↗
                        </a>
                        {inv.status === 'PAID' && (
                          <button
                            type="button"
                            onClick={() => handleIssueRefund(inv.id, inv.invoiceNumber, inv.totalAmount, inv.currency)}
                            className="text-[10px] font-black text-red-400 hover:text-red-300 bg-red-500/10 border border-red-500/20 px-2 py-0.5 rounded uppercase"
                          >
                            Issue Refund
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* SECTION 5: ADMIN COUPON MANAGER */}
        {metrics.adminCoupons && (
          <section className="glass-panel p-6 rounded-2xl border border-gold-500/30 bg-gold-500/5 relative space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <span className="text-gold-400 text-[8px] font-black uppercase tracking-[0.3em] block mb-1">Marketing & Campaigns</span>
                <h2 className="text-lg font-black uppercase text-white tracking-wider flex items-center gap-2">
                  <span>🎟️</span> Coupon & Promo Code Manager
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setShowCouponModal(true)}
                className="btn-gold text-[10px] font-black uppercase py-1.5 px-4 rounded-xl text-black"
              >
                + Create Promo Code
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {metrics.adminCoupons.map((c) => (
                <div key={c.id} className="bg-black/60 p-4 rounded-xl border border-white/10 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-black font-mono text-gold-400 tracking-wider">{c.id}</span>
                      <span className="text-[9px] font-black px-2 py-0.5 rounded bg-white/5 border border-white/10 uppercase">
                        {c.type === 'DISCOUNT_PERCENT' ? `${c.value}% OFF` : `${c.value}-Day Trial`}
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-400 mb-2">{c.description || 'Active Promo Code'}</p>
                    <p className="text-[9px] font-mono text-gray-500">Redemptions: {c.timesRedeemed || 0} times</p>
                  </div>
                  {!c.isSystem && (
                    <button
                      type="button"
                      onClick={() => handleDeleteCoupon(c.id)}
                      className="text-[9px] font-black text-red-400 hover:text-red-300 uppercase tracking-widest mt-4 text-left"
                    >
                      Delete Code ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* SECTION 6: SYSTEM OPERATOR DIAGNOSTICS */}
        {isOperator ? (
          <section className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-[10px] font-black uppercase text-gold-500 tracking-[0.2em] flex items-center gap-2">
                <svg className="w-4 h-4 text-gold-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"></path></svg>
                Operator Diagnostics (SysAdmin View)
              </h2>
              <span className="text-[7px] font-black text-gold-500 bg-gold/10 px-2 py-0.5 rounded border border-gold/20 uppercase tracking-widest">Privileged Access Stature</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              
              <div className="glass-panel p-5 rounded-xl bg-[#0a0a0a] space-y-4">
                <p className="text-[8px] font-black text-gray-500 uppercase tracking-widest border-b border-white/5 pb-2">Execution Latency</p>
                <div className="space-y-3">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400 font-medium">Avg Suggestion Pipeline</span>
                    <span className="text-white font-bold">{ops.averageAiResponseTimeMs} ms</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400 font-medium">Avg Digital Twin Build</span>
                    <span className="text-white font-bold">{ops.averageImageGenTimeMs} ms</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400 font-medium">Gateway Error Rate</span>
                    <span className={`font-bold ${ops.errorRatePercent > 2 ? 'text-red-500 animate-pulse' : 'text-emerald-400'}`}>
                      {ops.errorRatePercent}%
                    </span>
                  </div>
                </div>
              </div>

              <div className="glass-panel p-5 rounded-xl bg-[#0a0a0a] space-y-4">
                <p className="text-[8px] font-black text-gray-500 uppercase tracking-widest border-b border-white/5 pb-2">Infrastructure footprint</p>
                <div className="space-y-3">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400 font-medium">Active Closet Storage</span>
                    <span className="text-white font-bold">{formatBytes(ops.totalStorageUsedBytes)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400 font-medium">Active Users (24h)</span>
                    <span className="text-white font-bold">{ops.activeUsersCount24h} users</span>
                  </div>
                </div>
              </div>

              <div className="glass-panel p-5 rounded-xl bg-[#0a0a0a] space-y-4">
                <p className="text-[8px] font-black text-gray-500 uppercase tracking-widest border-b border-white/5 pb-2">Scaling Status</p>
                <div className="space-y-3">
                  <div className="text-[9px] font-bold text-emerald-400 uppercase tracking-wider flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full"></span> System Infrastructure: NOMINAL
                  </div>
                </div>
              </div>

            </div>
          </section>
        ) : (
          <section className="glass-panel p-6 rounded-2xl border border-dashed border-white/10 text-center opacity-40 py-8 bg-[#0a0a0a]">
            <p className="text-[9px] font-black text-gray-500 uppercase tracking-[0.2em] mb-1">Diagnostics Area Blocked</p>
            <p className="text-[8px] text-gray-600 uppercase font-bold tracking-widest">Sign in with an Operator / Admin account to view server footprint, latency logs, and anomaly warnings.</p>
          </section>
        )}

      </div>

      {/* CREATE PROMO CODE MODAL */}
      {showCouponModal && (
        <div className="fixed inset-0 z-[300] bg-black/90 flex items-center justify-center p-4">
          <form onSubmit={handleCreateCoupon} className="bg-[#05070c] border-2 border-gold-500 p-6 md:p-8 rounded-3xl max-w-md w-full space-y-5 text-white shadow-2xl">
            <div className="flex justify-between items-center border-b border-white/10 pb-3">
              <h3 className="text-lg font-black uppercase tracking-wider text-gold-400">Create New Promo Code</h3>
              <button type="button" onClick={() => setShowCouponModal(false)} className="text-gray-400 hover:text-white">✕</button>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase text-gray-400 tracking-wider block">Coupon Code</label>
              <input
                type="text"
                value={newCouponCode}
                onChange={(e) => setNewCouponCode(e.target.value.toUpperCase())}
                placeholder="e.g. SUMMER30"
                required
                className="bg-black/60 border border-white/10 rounded-xl px-3 py-2 text-xs font-mono text-white w-full focus:outline-none focus:border-gold-400 uppercase"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-wider block mb-1">Type</label>
                <select
                  value={newCouponType}
                  onChange={(e: any) => setNewCouponType(e.target.value)}
                  className="bg-black/60 border border-white/10 rounded-xl px-3 py-2 text-xs font-mono text-white w-full focus:outline-none focus:border-gold-400"
                >
                  <option value="DISCOUNT_PERCENT">Percentage Off (%)</option>
                  <option value="TRIAL_ACCESS">Free Trial Access (Days)</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-wider block mb-1">Value</label>
                <input
                  type="number"
                  value={newCouponValue}
                  onChange={(e) => setNewCouponValue(e.target.value)}
                  required
                  className="bg-black/60 border border-white/10 rounded-xl px-3 py-2 text-xs font-mono text-white w-full focus:outline-none focus:border-gold-400"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase text-gray-400 tracking-wider block">Description / Campaign Name</label>
              <input
                type="text"
                value={newCouponDesc}
                onChange={(e) => setNewCouponDesc(e.target.value)}
                placeholder="e.g. Summer Festival 30% Off"
                className="bg-black/60 border border-white/10 rounded-xl px-3 py-2 text-xs font-mono text-white w-full focus:outline-none focus:border-gold-400"
              />
            </div>

            <button type="submit" className="btn-gold w-full py-3 text-xs font-black uppercase tracking-widest text-black">
              Publish Promo Code
            </button>
          </form>
        </div>
      )}

    </div>
  );
};

export default Dashboard;
